import { useState } from "react";
import type { ReactNode } from "react";
import type { PayoutStructure, SeasonArchive } from "@averagejoesff/shared";
import { useAuth } from "../../auth/AuthProvider";
import { EditPayoutsForm } from "../admin/EditPayoutsForm";
import { ResourceStatus } from "../../components/ResourceStatus";
import { StateMessage } from "../../components/StateMessage";
import { useManagerNames } from "../../lib/useManagerNames";
import { useLeagueDocs } from "../../lib/LeagueDocsProvider";
import { PayoutAwardList } from "./PayoutAwardList";
import { PayoutSummary } from "./PayoutSummary";

interface PrizePayoutsViewProps {
  archive: SeasonArchive;
  /** The season picker, rendered alongside this view's own intro — see App.tsx. */
  toolbar?: ReactNode;
}

/**
 * The payout *structure* (categories/amounts) is still one site-wide
 * document (see `PayoutStructure`), edited independently of the selected
 * season via this view's own `editMode` toggle. But which team actually won
 * each category is per-season (`SeasonPayoutWinners`), and two categories
 * are resolved straight from that season's computed awards — so, unlike
 * before, this view now receives the season's `archive` like Blowout/Dues.
 */
export function PrizePayoutsView({ archive, toolbar }: PrizePayoutsViewProps) {
  const { isAuthenticated, idToken } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const { payouts, setPayouts, payoutWinners: winners } = useLeagueDocs();
  const { labelByTeam } = useManagerNames(archive);

  // Pushes the write response into the shared copy (see LeagueDocsProvider)
  // rather than re-fetching, which could hand back the CDN's pre-write copy.
  function handleSaved(updated: PayoutStructure) {
    setEditMode(false);
    setPayouts(updated);
  }

  return (
    <section className="feature" aria-labelledby="payouts-heading">
      <div className="feature__intro">
        <div className="feature__intro-row">
          <h2 id="payouts-heading" className="feature__title">
            Prize Payouts
          </h2>
          {toolbar}
        </div>
        <p className="feature__description">
          How the {archive.season} season's buy-in pool gets paid out.
        </p>
      </div>

      {isAuthenticated && idToken && (
        <button
          type="button"
          className="feature__edit-toggle"
          onClick={() => setEditMode((v) => !v)}
        >
          {editMode ? "Done editing" : "Edit payout structure"}
        </button>
      )}

      <ResourceStatus
        resource={payouts}
        loading="Loading payout structure…"
        error="Could not load the payout structure"
      />

      {payouts.status === "ready" && editMode && idToken && (
        <section className="admin-panel" aria-label="Edit prize payout structure">
          <p className="admin-panel__hint">
            This is the league's shared list of categories and dollar amounts — it
            applies to every season. To assign this season's winners, use "Edit
            Season" → "Prizes" instead.
          </p>
          <EditPayoutsForm payouts={payouts.data} idToken={idToken} onSaved={handleSaved} />
        </section>
      )}

      {payouts.status === "ready" &&
        !editMode &&
        (payouts.data === null ? (
          <StateMessage
            variant="empty"
            title="No payout structure published yet"
            detail={
              isAuthenticated
                ? 'Click "Edit payout structure" above to set it up.'
                : "Check back once the league commissioner publishes it."
            }
          />
        ) : (
          <>
            <PayoutSummary
              buyInPerTeam={payouts.data.buyInPerTeam}
              totalPool={payouts.data.totalPool}
            />
            <h3 className="feature__subhead">Awards</h3>
            <ResourceStatus
              resource={winners}
              loading="Loading this season's winners…"
              error="Could not load this season's winners"
            />
            {winners.status === "ready" && (
              <PayoutAwardList
                awards={payouts.data.awards}
                note={payouts.data.note}
                winners={winners.data?.winners ?? []}
                weeks={archive.awards.biggestBlowout?.weeks ?? []}
                standings={archive.awards.seasonStandings?.standings ?? []}
                labelByTeam={labelByTeam}
              />
            )}
          </>
        ))}
    </section>
  );
}
